import React, { useEffect, useState } from 'react';
import { Spin, Radio, Divider, Typography, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import styled from 'styled-components';

import AgGrid from "../../../../../../components/ag-grid-table/index";
import PlacementCard from '../../../../../../components/uiComponent/placement-card';
import Button from '../../../../../../components/uiComponent/button/index';
import ProductImg from '../../../../../../assets/images/newImages/nestle.svg';
import useResponsiveTableHeight from '../../../../../../hooks/useResponsiveTableHeight';

import { ShippingWrapper } from '../style';
import { ListPlacementOptions } from '../../../../../../slices/post-shipment';

const PlacementOptionWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    grid-gap: 20px;
    width: 100%;

    .product-item-wrapper {
        display: flex;
        align-items: flex-start;
        grid-gap: 10px;
        img {
            width: 40px;
            height: 28px;
            object-fit: contain;
        }
        .product-item {
            width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    }
    .ag-cell {
        &:not(:first-child) {
            border-left: 1px solid #ECEFF1;
        }
    }
    footer {
        display: flex;
        justify-content: end;
        align-items: center;
        margin-top: 12px;
        grid-gap: 16px;
    }
    .ant-spin-nested-loading {
        position: relative;
        width: 100%;
    }

    .sidebar-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 331px;
        width: 100%;
        grid-gap: 12px;
    }

    .cards-overlay-wrapper {
        background-color: #fff;
        border: 1px solid #ECEFF1;
        border-radius: 4px;
        padding: 8px;
        min-height: 300px;
        height: auto;
        max-height: calc(100vh - 136px);
        overflow: auto;
        max-width: 331px;
        width: 100%;
        &.loading-accured {
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            span.ant-spin-dot.ant-spin-dot-spin {
                margin-top: -20px;
            }
            .ant-spin-text {
                left: 0px;
            }
        }
    }
    .content-area-wrapper {
        width: calc(100% - 340px);
        .tabs-cards-wrapper {
            background: #E6F4FC;
            padding: 14px 12px;
            border-radius: 4px;
            width: 100%;
            display: flex;
            overflow: auto;
            grid-gap: 12px;
            margin-bottom: 9px;
            &::-webkit-scrollbar {
                display: none;
            }
            .tab-item-wrapper {
                display: grid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
                max-width: 350px;
                min-width: 350px;
                grid-gap: 8px;
                width: 100%;
                background: #F5FBFF;
                padding: 12px;
                border-radius: 4px;
                border: 1px solid transparent;
                cursor: pointer;
                &.active {
                    background: #fff;
                    border-color: #1565D8;
                }
                .tab-item {
                    display: flex;
                    flex-direction: column;
                    grid-row-gap: 2px;
                    p {
                        margin: 0px;
                        font-weight: 400;
                        font-size: 10px;
                        line-height: 12px;
                        letter-spacing: 0%;
                        color: #808183;
                        &:not(:first-child) {
                            font-weight: 600;
                            color: #535E63;
                        }
                    }
                }
            }
        }
    }
`;

const Step1 = ({ setActiveKey, onPlacementSelect, confirmingPlacementOption, handleConfirmPlacementOption, selectedPlacementOptionId, onCancelPlan, cancelingInboundPlan }) => {
  const dispatch = useDispatch();
  const { workOrderId } = useParams();

  const [selectedPlacementIndex, setSelectedPlacementIndex] = useState(0);
  const [selectedShipmentIndex, setSelectedShipmentIndex] = useState(0);

  const {
    shipmentDetails,
    placementOptions,
    listingPlacementOptions,
  } = useSelector((state) => state.postShipment || {});
  const { packingMethod, shipments } = shipmentDetails || {};

  useEffect(() => {
    if (packingMethod === 'pack_now' && isEmpty(placementOptions) && isEmpty(shipments)) {
      dispatch(ListPlacementOptions({ workOrderId }));
    }
  }, [packingMethod, workOrderId]);

  useEffect(() => {
    setSelectedPlacementIndex(0);
    setSelectedShipmentIndex(0);
  }, [placementOptions?.length]);

  useEffect(() => {
    const id = placementOptions?.[selectedPlacementIndex]?.placementOptionId || null;
    onPlacementSelect?.(id);
  }, [selectedPlacementIndex, placementOptions]);

  const { tableHeight, tableSectionRef, paginationRef } = useResponsiveTableHeight({
    minHeight: 220,
    bottomSpacing: 100,
  });
  const selectedPlacement = placementOptions?.[selectedPlacementIndex] || {};
  const selectedShipment = selectedPlacement?.shipmentDetails?.[selectedShipmentIndex] || {};

  if (listingPlacementOptions) {
    return (
      <ShippingWrapper className='step1' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin tip='Loading...' spinning />
      </ShippingWrapper>
    );
  }

  // If we already have confirmed shipments, show the grid
  if (!isEmpty(shipments)) {
    return (
      <ShippingWrapper className='step1'>
        <div className='tabs-cards-wrapper'>
          {shipments.map((shipment) => (
            <div key={shipment.amazon_shipment_id} className="tab-item-wrapper active">
              <div className='tab-item'>
                <p>Shipment ID</p>
                <p>{shipment.amazon_shipment_confirmation_id}</p>
              </div>
              <div className='tab-item'>
                <p>Destination</p>
                <p>{shipment.amazon_warehouse_id}</p>
              </div>
              <div className='tab-item'>
                <p>SKUs Count</p>
                <p>{shipment.shipment_items_info?.length || 0}</p>
              </div>
              <div className='tab-item'>
                <p>Mileage (mi)</p>
                <p>{shipment.miles || '---'}</p>
              </div>
            </div>
          ))}
        </div>
        <AgGrid
          user_referrals={shipments.flatMap(s => (s.shipment_items_info || []).map((item, index) => ({
            ...item,
            __rowId: `${s.amazon_shipment_id}-${item.id || item.msku || item.sku || index}`
          })))}
          columnDefinitions={[
            {
              headerName: 'Items',
              flex: 1,
              minWidth: 200,
              cellRenderer: ({ data }) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                  <img src={data.image_url || ProductImg} alt="Product" style={{ width: 40, height: 40, objectFit: 'contain', border: '1px solid #eceff1', borderRadius: '4px' }} />
                  <span style={{ fontSize: '12px', fontWeight: '500' }}>{data.item_name || data.msku}</span>
                </div>
              )
            },
            { headerName: 'SKU', field: 'msku', flex: 1 },
            { headerName: 'FNSKU', field: 'fnsku', flex: 1 },
            { headerName: 'Quantity', field: 'quantity', flex: 1 }
          ]}
          rowHeight={64}
          headerHeight={36}
          containerHeight={tableHeight}
          getRowNodeId={({ __rowId }) => __rowId}
        />
      </ShippingWrapper>
    );
  }

  return (
    <ShippingWrapper className='step1'>
      <PlacementOptionWrapper>
        <div className="sidebar-wrapper">
          <div className={`cards-overlay-wrapper ${listingPlacementOptions || confirmingPlacementOption ? 'loading-accured' : ''}`}>
            <Spin wrapperClassName="placement-cards-loading" tip='Loading...' spinning={listingPlacementOptions || confirmingPlacementOption}>
              <PlacementCard
                cardsData={placementOptions?.map((opt, idx) => ({
                  id: opt.placementOptionId,
                  placementOption: `Placement Option ${idx + 1}`,
                  shipments: opt.shipmentDetails?.length || 0,
                  discount: opt.discounts?.reduce((total, fee) => total + (fee?.value?.amount || 0), 0) || 0,
                  expiry: opt.expiration ? dayjs(opt.expiration).format('M/D/YY') : '',
                  placementFee: opt.fees?.reduce((total, fee) => total + (fee?.value?.amount || 0), 0) || 0
                }))}
                activePlacementIndex={selectedPlacementIndex}
                selectPlacementOption={(index) => {
                  setSelectedPlacementIndex(index);
                  setSelectedShipmentIndex(0);
                }}
              />
            </Spin>
          </div>
        </div>

        <div className='content-area-wrapper'>
          <Spin tip='Loading...' wrapperClassName="placement-cards-loading" spinning={listingPlacementOptions || confirmingPlacementOption}>
            <div className='tabs-cards-wrapper'>
              {selectedPlacement?.shipmentDetails?.map((item, index) => (
                <div key={item.shipmentId}
                  className={`tab-item-wrapper ${selectedShipmentIndex === index ? 'active' : ''}`}
                  onClick={() => setSelectedShipmentIndex(index)}>
                  <div className='tab-item'>
                    <p>FC ID</p>
                    <p>{item?.fcId || item?.destinationCenterId || '---'}</p>
                  </div>
                  <div className='tab-item'>
                    <p>SKUs Count</p>
                    <p>{item?.items?.length || 0}</p>
                  </div>
                  <div className='tab-item'>
                    <p>No of bundles</p>
                    <p>
                      {item?.items?.reduce((sum, i) => sum + (i?.quantity || 0), 0)}
                    </p>
                  </div>
                  <div className='tab-item'>
                    <p>Mileage (mi)</p>
                    <p>{item?.miles || '---'}</p>
                  </div>
                </div>
              ))}
            </div>

            <div ref={tableSectionRef}>
              <AgGrid
                key={`${selectedPlacement?.placementOptionId || selectedPlacementIndex}-${selectedShipment?.shipmentId || selectedShipmentIndex}`}
                user_referrals={selectedShipment?.items?.map((item, index) => ({
                  ...item,
                  image_url: item.image_url || ProductImg,
                  title: item.item_name || item.msku,
                  __rowId: `${selectedShipment?.shipmentId || selectedShipmentIndex}-${item.msku || item.sku || item.asin || index}`
                })) || []}
                defaultColDef={{
                  resizable: true,
                  sortable: false,
                  filter: false,
                  suppressRowClickSelection: true,
                  suppressMenu: true,
                  suppressMovable: true
                }}
                columnDefinitions={[
                  {
                    headerName: 'Items',
                    flex: 2,
                    minWidth: 250,
                    cellRenderer: ({ data }) => (
                      <div className='product-item-wrapper'>
                        <img src={data.image_url || ProductImg} alt="Product" />
                        <span className='product-item'>{data.title || '---'}</span>
                      </div>
                    )
                  },
                  { headerName: 'SKU', field: 'msku', flex: 1, minWidth: 120 },
                  { headerName: 'ASIN', field: 'asin', flex: 1, minWidth: 120 },
                  { headerName: 'Quantity', field: 'quantity', flex: 1, minWidth: 100 }
                ]}
                rowHeight={64}
                headerHeight={36}
                containerHeight={tableHeight}
                className="sku-table"
                getRowNodeId={({ __rowId }) => __rowId}
              />
            </div>

            <div ref={paginationRef}>
              <footer>
                {packingMethod === 'pack_now' && (
                  <Button
                    text="Cancel Plan"
                    className="danger mb-0"
                    minWidth="80px"
                    size="small"
                    disabled={cancelingInboundPlan}
                    onClick={onCancelPlan}
                  />
                )}
                <Tooltip title={!selectedPlacementOptionId ? 'Please select a placement option' : ''} placement='top'>
                  <span>
                    <Button
                      text="Confirm Placement Option"
                      className="mb-0"
                      minWidth="80px"
                      size="small"
                      disabled={confirmingPlacementOption || !selectedPlacementOptionId}
                      onClick={handleConfirmPlacementOption}
                    />
                  </span>
                </Tooltip>
              </footer>
            </div>
          </Spin>
        </div>
      </PlacementOptionWrapper>
    </ShippingWrapper>
  );
};

export default Step1;
